import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { isAdmin } from '../lib/roles';
import {
  IconAudit,
  IconBuilding,
  IconCampaign,
  IconChart,
  IconHome,
  IconLink,
  IconLogout,
  IconMoon,
  IconSettings,
  IconShield,
  IconSun,
  IconUsers,
} from './Icons';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

const customerNav: NavItem[] = [
  { to: '/app', label: 'Overview', icon: <IconHome />, end: true },
  { to: '/app/links', label: 'Links', icon: <IconLink /> },
  { to: '/app/campaigns', label: 'Campaigns', icon: <IconCampaign /> },
  { to: '/app/settings', label: 'Settings', icon: <IconSettings /> },
];

const adminNav: NavItem[] = [
  { to: '/admin', label: 'Overview', icon: <IconHome />, end: true },
  { to: '/admin/users', label: 'Users', icon: <IconUsers /> },
  { to: '/admin/orgs', label: 'Organizations', icon: <IconBuilding /> },
  { to: '/admin/links', label: 'Links', icon: <IconLink /> },
  { to: '/admin/campaigns', label: 'Campaigns', icon: <IconCampaign /> },
  { to: '/admin/analytics', label: 'Analytics', icon: <IconChart /> },
  { to: '/admin/audit', label: 'Audit log', icon: <IconAudit /> },
  { to: '/admin/settings', label: 'Settings', icon: <IconSettings /> },
];

interface LayoutProps {
  variant: 'customer' | 'admin';
}

export function Layout({ variant }: LayoutProps) {
  const { user, organization, signOut } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const items = variant === 'admin' ? adminNav : customerNav;

  return (
    <div className="min-h-full grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:flex flex-col border-r border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900">
        <Link
          to="/app"
          className="px-5 py-5 border-b border-ink-200 dark:border-ink-800 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800/40 transition"
          aria-label="Go to dashboard"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 text-white font-bold">
            S
          </span>
          <div>
            <div className="text-sm font-semibold tracking-tight">SLS Short</div>
            <div className="text-xs text-ink-500 dark:text-ink-400">
              {variant === 'admin' ? 'Admin console' : organization?.name ?? 'Workspace'}
            </div>
          </div>
        </Link>
        <nav className="flex-1 px-2 py-4 space-y-0.5">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
                  isActive
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200 font-semibold'
                    : 'text-ink-600 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800'
                }`
              }
            >
              <span className="flex-shrink-0">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
          {/*
            Admin console entry point in the customer sidebar.
            Gated by `isAdmin()` so it is only visible to users whose role is
            `admin` or `super_admin`. The matching route guard in App.tsx
            (`<ProtectedRoute minRole="admin">`) blocks direct navigation for
            anyone else; this gate is purely a UX affordance.
           */}
          {variant === 'customer' && isAdmin(user) && (
            <NavLink
              to="/admin"
              className="mt-4 flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-ink-600 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800"
            >
              <IconShield />
              Admin console
            </NavLink>
          )}
          {/*
            Symmetric "Back to app" link in the admin sidebar so admins can
            return to the customer console. We don't gate this — anyone in
            the admin shell already passed the route's role check.
           */}
          {variant === 'admin' && (
            <NavLink
              to="/app"
              end
              className="mt-4 flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-ink-600 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800"
            >
              <span aria-hidden="true" className="text-base leading-none">
                ←
              </span>
              Back to app
            </NavLink>
          )}
        </nav>
        <div className="px-3 py-4 border-t border-ink-200 dark:border-ink-800 space-y-2">
          <div className="px-3 py-2 rounded-lg bg-ink-50 dark:bg-ink-800">
            <div className="text-sm font-medium text-ink-900 dark:text-ink-100 truncate">
              {user?.name ?? user?.email}
            </div>
            <div className="text-xs text-ink-500 dark:text-ink-400 truncate">{user?.email}</div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost flex-1"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <IconSun /> : <IconMoon />}
              <span className="text-xs">{theme === 'dark' ? 'Light' : 'Dark'}</span>
            </button>
            <button
              type="button"
              className="btn-ghost flex-1"
              onClick={async () => {
                await signOut();
                navigate('/login');
              }}
            >
              <IconLogout />
              <span className="text-xs">Sign out</span>
            </button>
          </div>
        </div>
      </aside>
      <main className="flex flex-col min-h-screen">
        <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between px-4 py-3 bg-white dark:bg-ink-900 border-b border-ink-200 dark:border-ink-800">
          <Link to="/app" className="flex items-center gap-2 font-semibold" aria-label="Go to dashboard">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-brand-500 to-violet-600 text-white text-xs font-bold">
              S
            </span>
            SLS Short
            {variant === 'admin' && (
              <span className="ml-1 px-1.5 py-0.5 rounded bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-200 text-[10px] uppercase tracking-wide">
                Admin
              </span>
            )}
          </Link>
          <div className="flex items-center gap-1">
            {variant === 'admin' && (
              <Link to="/app" className="btn-ghost text-xs" aria-label="Back to app">
                <span aria-hidden="true">←</span>
                <span>App</span>
              </Link>
            )}
            <button
              type="button"
              className="btn-ghost"
              onClick={async () => {
                await signOut();
                navigate('/login');
              }}
              aria-label="Sign out"
            >
              <IconLogout />
            </button>
          </div>
        </header>
        <div className="flex-1 px-4 lg:px-8 py-6 max-w-7xl w-full mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
